import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import * as moderatorApi from './moderatorApi';
import type {
  AppealResolution,
  AppealRow,
  AuditEntry,
  BlobEntry,
  DecisionPayload,
  ModeratorSubmissionRow,
  ReportEntry,
  SuspendedUserSummary,
} from './moderatorApi';

/**
 * TanStack Query hooks for the moderator dashboard. Cache keys are
 * scoped under `['moderator', ...]` so a sign-out invalidation that
 * targets `['account']` doesn't accidentally drop them, but a sign-out
 * navigation away from /admin/moderator does fully unmount.
 */

const MODERATOR_KEY = ['moderator'] as const;

export function useModeratorSubmissions(
  statusFilter?: ModeratorSubmissionRow['status'],
): UseQueryResult<ModeratorSubmissionRow[], Error> {
  return useQuery({
    queryKey: [...MODERATOR_KEY, 'submissions', statusFilter ?? 'all'],
    queryFn: () => moderatorApi.listModeratorSubmissions(statusFilter),
    // Moderation work is real-time-ish — short stale time so a fresh
    // submit shows up quickly without the moderator hitting refresh.
    staleTime: 5_000,
  });
}

export function useSubmissionAudit(
  submissionId: string | null,
): UseQueryResult<AuditEntry[], Error> {
  return useQuery({
    queryKey: [...MODERATOR_KEY, 'submission', submissionId, 'audit'],
    queryFn: () => moderatorApi.getSubmissionAudit(submissionId as string),
    enabled: submissionId !== null,
  });
}

export function useSubmissionBlobs(
  submissionId: string | null,
): UseQueryResult<BlobEntry[], Error> {
  return useQuery({
    queryKey: [...MODERATOR_KEY, 'submission', submissionId, 'blobs'],
    queryFn: () => moderatorApi.getSubmissionBlobs(submissionId as string),
    enabled: submissionId !== null,
  });
}

export function useSubmissionReports(
  submissionId: string | null,
): UseQueryResult<ReportEntry[], Error> {
  return useQuery({
    queryKey: [...MODERATOR_KEY, 'submission', submissionId, 'reports'],
    queryFn: () => moderatorApi.getSubmissionReports(submissionId as string),
    enabled: submissionId !== null,
  });
}

interface DecideArgs {
  submissionId: string;
  payload: DecisionPayload;
}

export function useDecideSubmission(): UseMutationResult<{ status: string }, Error, DecideArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, payload }: DecideArgs) =>
      moderatorApi.decideSubmission(submissionId, payload),
    onSuccess: (_data, args) => {
      // Invalidate the submissions list so the row picks up the new
      // status, and the audit chain so the override row appears.
      void qc.invalidateQueries({ queryKey: [...MODERATOR_KEY, 'submissions'] });
      void qc.invalidateQueries({
        queryKey: [...MODERATOR_KEY, 'submission', args.submissionId],
      });
      // Public gallery may have changed too (an approve flips it
      // visible, a reject removes it).
      void qc.invalidateQueries({ queryKey: ['gallery'] });
    },
  });
}

// --- Suspension + appeals ---

const SUSPENDED_KEY = [...MODERATOR_KEY, 'suspended-users'] as const;
const APPEALS_KEY = [...MODERATOR_KEY, 'appeals'] as const;

export function useSuspendedUsers(): UseQueryResult<SuspendedUserSummary[], Error> {
  return useQuery({
    queryKey: SUSPENDED_KEY,
    queryFn: moderatorApi.listSuspendedUsers,
    staleTime: 5_000,
  });
}

export function useSuspendUser(): UseMutationResult<
  SuspendedUserSummary,
  Error,
  { userId: string; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }) => moderatorApi.suspendUser(userId, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUSPENDED_KEY });
    },
  });
}

export function useUnsuspendUser(): UseMutationResult<
  SuspendedUserSummary,
  Error,
  { userId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) => moderatorApi.unsuspendUser(userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUSPENDED_KEY });
      // The kid's own /me view of `suspended` should also refresh.
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useAppeals(pendingOnly = true): UseQueryResult<AppealRow[], Error> {
  return useQuery({
    queryKey: [...APPEALS_KEY, pendingOnly ? 'pending' : 'all'],
    queryFn: () => moderatorApi.listAppeals(pendingOnly),
    staleTime: 5_000,
  });
}

export function useResolveAppeal(): UseMutationResult<
  AppealRow,
  Error,
  { appealId: string; payload: AppealResolution }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appealId, payload }) => moderatorApi.resolveAppeal(appealId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: APPEALS_KEY });
      void qc.invalidateQueries({ queryKey: SUSPENDED_KEY });
    },
  });
}
